// Auftragsdetail mit den zwei Schreib-Aktionen, gegatet ueber Funktionsrechte:
//   - canConfirm  -> Zeit auf einen Arbeitsgang buchen (Rueckmeldung), idempotent pro Submit
//   - canApprove  -> einen CRTD-Auftrag freigeben (Freigabe; PROPOSED contract addition, §4a)
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/OrderDetail.jsx). Geaendert wurden NUR
// Styling-Idiom (v4-Token -> isDarkMode-Verzweigung), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix),
// Import-/API-Pfade und die Fehlerbehandlung:
//   - Prototyp: api.getOrder() -> { status, data }; 404 wurde am status erkannt.
//   - Monolith: getOrder(user, id) liefert den Body DIREKT und WIRFT bei !ok (404). Daher wird
//     "nicht gefunden" ueber den gefangenen Fehler erkannt, nicht ueber einen Status-Code.
//   - releaseOrder/confirmOperation liefern den Body direkt (res?.ok statt { data }).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, CheckCircle2, Package } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { getOrder, releaseOrder, tecoOrder, closeOrder, confirmOperation, addOrderComponents, searchMaterials, materialStock } from '../api';
import { recordRecent } from '../recency';
import { Button, Card, StatusBadge } from './ui';
import RecentMaterialChips from './RecentMaterialChips';
import SapPayloadPanel from './SapPayloadPanel';
import CostsPanel from './order/CostsPanel';
import GoodsIssuePanel from './order/GoodsIssuePanel';
import AvailabilityPanel from './order/AvailabilityPanel';
import AssigneePanel from './order/AssigneePanel';
import ConfirmationsPanel from './order/ConfirmationsPanel';

function OrderDetail({ me, user, orderId, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { canConfirm, canApprove, canTechnicallyComplete, canBusinessComplete } = me.profile.functionRights;
  const role = me.profile.id;

  // seq-Guard: load() wird auch nach Schreibaktionen aufgerufen (onPosted/onReleased); nur die
  // Antwort des JUENGSTEN Requests darf den State setzen. NUR ein 404 heisst "nicht gefunden" —
  // Transport-/Serverfehler bekommen einen eigenen, erneut versuchbaren Fehlerzustand.
  const seq = useRef(0);
  const load = useCallback(() => {
    const mySeq = ++seq.current;
    setLoadError(false);
    getOrder(user, orderId, role)
      .then((data) => { if (seq.current === mySeq) setOrder(data); })
      .catch((e) => {
        if (seq.current !== mySeq) return;
        if (e?.status === 404) setMissing(true);
        else setLoadError(true);
      });
  }, [user, orderId, role]);

  useEffect(() => { load(); }, [load]);

  if (missing) {
    return (
      <div>
        <BackBtn onBack={onBack} label={t('pm.common.back')} isDarkMode={isDarkMode} />
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.detail.notFound')}</p>
      </div>
    );
  }
  if (loadError && !order) {
    return (
      <div>
        <BackBtn onBack={onBack} label={t('pm.common.back')} isDarkMode={isDarkMode} />
        <div className="py-10 text-center space-y-3">
          <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>{t('pm.common.loadError')}</p>
          <button onClick={load} className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {t('pm.common.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (!order) return <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>;

  return (
    <div className="space-y-4">
      <BackBtn onBack={onBack} label={t('pm.common.back')} isDarkMode={isDarkMode} />

      <Card className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.detail.title')} {order.orderId}</div>
            <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{order.description}</div>
          </div>
          <StatusBadge status={order.status} />
        </div>
        <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {order.orderType} · {order.workCenter}{order.plannedStart ? ` · ${order.plannedStart}` : ''}
          {order.equipmentId ? ` · ${order.equipmentId}` : ''}
        </div>
        {order.longText && <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{order.longText}</p>}
      </Card>

      {/* SAP-Transparenz: die rohen Felder, wie sie aus SAP (Mock) kamen — inkl. Status-CODE,
          waehrend der Badge oben die uebersetzte Anzeige traegt. */}
      <SapPayloadPanel
        showLabel={t('pm.sap.show')}
        hideLabel={t('pm.sap.hide')}
        note={t(me.sap?.live ? 'pm.sap.fromSapNote' : 'pm.sap.fromSapNoteMock')}
        rows={[
          { label: t('pm.detail.title'), value: order.orderId },
          { label: t('pm.order.fieldOrderType'), value: order.orderType, info: t('pm.info.orderType') },
          { label: 'Status', value: order.status, info: t('pm.info.status') },
          // SAP's full active-status string ("PRC SSAP NMAT REL PCNF"). `status` above is the ONE
          // code the adapter collapses it to (ORDER_STATUS_PRECEDENCE), which drops exactly the
          // codes that later explain a refused TECO — MANC/NMAT. The adapter has returned
          // statusDetail all along and nothing read it (PM-SAP-FIELD-TRANSPARENCY.md §6.3).
          // Rendered only when it adds something: SapPayloadPanel drops undefined rows.
          {
            label: t('pm.detail.sapStatus'),
            value: order.statusDetail && order.statusDetail !== order.status ? order.statusDetail : undefined,
          },
          { label: t('pm.order.fieldPlant'), value: order.plant, info: t('pm.info.plant') },
          { label: t('pm.order.fieldWorkCenter'), value: order.workCenter, info: t('pm.info.workCenter') },
          { label: t('pm.order.fieldDevice'), value: order.equipmentId, info: t('pm.info.equipment') },
          { label: t('pm.order.fieldPriority'), value: order.priority, info: t('pm.info.priority') },
          { label: t('pm.sap.plannedStart'), value: order.plannedStart, info: t('pm.info.plannedDates') },
          { label: t('pm.sap.plannedEnd'), value: order.plannedEnd, info: t('pm.info.plannedDates') },
          { label: t('pm.detail.maintenancePlan'), value: order.maintenancePlan, info: t('pm.info.maintenancePlan') },
          { label: t('pm.detail.notification'), value: order.notificationId, info: t('pm.info.notification') },
        ]}
      />

      {order.maintenancePlan && (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${isDarkMode
          ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
          {t('pm.badge.preventive')} · {order.maintenancePlan}
        </span>
      )}

      {/* Who owns this order (portal-side) + are the planned parts actually on stock — both
          BEFORE the release decision, which is where they matter. */}
      <AssigneePanel user={user} role={role} order={order} canAssign={canApprove} onChanged={load} />
      {order.status === 'CRTD' && (
        <AvailabilityPanel user={user} role={role} order={order} />
      )}

      {/* Lebenszyklus-Aktionen in SAP-Reihenfolge: CRTD -> REL -> TECO -> CLSD. Sichtbar nur mit
          dem passenden Recht UND im passenden Status — dieselbe Reihenfolge erzwingt SAP selbst
          (ein nicht freigegebener Auftrag laesst sich nicht technisch abschliessen, ein nicht
          endrueckgemeldeter nicht kaufmaennisch). Die UI blendet nur aus; autoritativ ist das
          requireRight-Gate im Backend. */}
      {canApprove && order.status === 'CRTD' && (
        <LifecyclePanel user={user} role={role} orderId={order.orderId} onDone={load}
          call={releaseOrder} labelKey="pm.detail.release" busyKey="pm.detail.releasing" doneKey="pm.detail.releasedNote" />
      )}
      {canTechnicallyComplete && ['REL', 'PCNF', 'CNF'].includes(order.status) && (
        <LifecyclePanel user={user} role={role} orderId={order.orderId} onDone={load}
          call={tecoOrder} labelKey="pm.detail.teco" busyKey="pm.detail.tecoing" doneKey="pm.detail.tecoNote" />
      )}
      {canBusinessComplete && order.status === 'TECO' && (
        <LifecyclePanel user={user} role={role} orderId={order.orderId} onDone={load}
          call={closeOrder} labelKey="pm.detail.close" busyKey="pm.detail.closing" doneKey="pm.detail.closedNote"
          hintKey="pm.detail.closeHint" />
      )}

      <section>
        <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
          {t('pm.detail.operations')}
        </h3>
        <div className="space-y-2">
          {order.operations.map((op) => (
            <OperationRow key={op.operationNo} user={user} role={role} orderId={order.orderId} op={op} canConfirm={canConfirm} onPosted={load} />
          ))}
        </div>
        {/* Directly under the operations, because it answers the question the operation row raises:
            "0/4 H confirmed" — by whom, when, and can I undo mine? */}
        <div className="mt-2">
          <ConfirmationsPanel user={user} role={role} orderId={order.orderId}
            canConfirm={canConfirm} onChanged={load} />
        </div>
      </section>

      <section>
        <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
          {t('pm.detail.components')}
        </h3>
        <div className="space-y-2">
          {order.components.map((c) => (
            <Card key={c.itemNo} className="flex items-center gap-3">
              <Package className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} size={20} />
              <div className="min-w-0">
                <div className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{c.description || c.materialId}</div>
                <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {c.materialId} · {t('pm.detail.qty')} {c.requiredQty} {c.unit}
                </div>
                {/* Ergebnis der SAP-Folgeverarbeitung (PROPOSED-Felder): Reservierung immer,
                    BANF-Chip nur bei Unterdeckung. Fehlen die Felder (fremder Adapter), bleibt die Zeile weg. */}
                {(c.reservationNo || c.purchaseRequisitionNo || c.purchaseRequisition) && (
                  <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                    {c.reservationNo && (
                      <span className={`rounded px-1.5 py-0.5 ${isDarkMode ? 'bg-white/[0.08] text-white/60' : 'bg-slate-100 text-slate-500'}`}>
                        {t('pm.comp.resShort')} {c.reservationNo}
                      </span>
                    )}
                    {(c.purchaseRequisitionNo || c.purchaseRequisition) && (
                      <span className={`rounded px-1.5 py-0.5 font-medium ${isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}
                        title={t('pm.comp.prBridge')}>
                        {t('pm.comp.prShort')} {c.purchaseRequisitionNo || c.purchaseRequisition}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
        {me.profile.functionRights.canAddComponents && (
          <AddComponentPanel user={user} role={role} order={order} onAdded={load} />
        )}
        {/* Physical consumption (MIGO 261) and the money view — split into components/order/
            because this file already carries the lifecycle (500-LOC rule). */}
        {me.profile.functionRights.canAddComponents && (
          <GoodsIssuePanel user={user} role={role} order={order} onPosted={load} />
        )}
        {me.profile.functionRights.canViewCosts && (
          <CostsPanel user={user} role={role} orderId={order.orderId} />
        )}
      </section>
    </div>
  );
}

// Zurueck-Affordanz — gedaempfter Pfeil-Link.
function BackBtn({ onBack, label, isDarkMode }) {
  return (
    <button onClick={onBack} className={`flex items-center gap-1 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
      <ArrowLeft size={18} /> {label}
    </button>
  );
}

// Lebenszyklus-Panel: Freigabe (CRTD->REL), technischer Abschluss (REL->TECO) und kaufmaennischer
// Abschluss (TECO->CLSD) unterscheiden sich nur in Aufruf und Beschriftung — eine Komponente statt
// drei fast gleicher. Ein Idempotency-Key pro Panel-Session: ein Doppeltipp darf nie doppelt buchen.
// `hintKey` steht ueber dem Button (der kaufmaennische Abschluss ist unumkehrbar).
function LifecyclePanel({ user, role, orderId, onDone, call, labelKey, busyKey, doneKey, hintKey }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const idemKey = useRef(crypto.randomUUID());

  const run = async () => {
    setBusy(true); setError('');
    try {
      // pmCall liefert auch Business-Failures als Body ({ok:false, error}) — res.error ist die
      // echte Backend-/SAP-Meldung, nicht mehr nur "HTTP error! status: 400".
      const res = await call(user, orderId, idemKey.current, role);
      if (res?.ok) { setDone(true); onDone(); }
      else setError(res?.error || 'error');
    } catch (e) {
      setError(e?.message || 'error');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card className={`flex items-center gap-2 ${isDarkMode
        ? 'bg-emerald-500/10 text-emerald-300'
        : 'bg-emerald-50 text-emerald-700'
      }`}>
        <CheckCircle2 size={20} /> {t(doneKey)}
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {hintKey && (
        <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{t(hintKey)}</p>
      )}
      <Button variant="confirm" onClick={run} disabled={busy}>
        {busy ? t(busyKey) : (<><Check /> {t(labelKey)}</>)}
      </Button>
      {error && (
        <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.order.errorPrefix')}: {error}</p>
      )}
    </div>
  );
}

// Komponente hinzufuegen: Material suchen -> Verfuegbarkeit im Auftragswerk pruefen -> an SAP
// uebergeben. Reservierung/BANF erzeugt SAP selbst (Prozessentscheidung 2026-07-03); die
// zurueckgemeldeten messages zeigen sofort, was entstanden ist, und onAdded() laedt den Auftrag
// neu (Komponente traegt dann Reservierungs-/BANF-Nummer). Ein Idempotency-Key je Uebergabe;
// Rotation nach Erfolg (naechste Uebergabe = neue Aktion) UND nach Business-Failure.
function AddComponentPanel({ user, role, order, onAdded }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stock, setStock] = useState(null); // number | 'error' | null (noch nicht geladen)
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState('');
  const idemKey = useRef(crypto.randomUUID());
  const timer = useRef();
  const seq = useRef(0);

  // Debounced-Suche wie MaterialSearch (seq-Guard: nur die juengste Antwort zaehlt).
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!open || !q.trim() || selected) { setResults([]); return; }
    timer.current = setTimeout(() => {
      const mySeq = ++seq.current;
      searchMaterials(user, q.trim(), role)
        .then((res) => { if (seq.current === mySeq) setResults(res?.items || []); })
        .catch(() => { if (seq.current === mySeq) setResults([]); });
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, user, role, open, selected]);

  const pick = (m) => {
    setSelected(m);
    setResults([]);
    setStock(null);
    // Nutzungssignal fuer die Zuletzt-verwendet-Liste (fire-and-forget).
    recordRecent(user, 'materials', {
      materialId: m.materialId, description: m.description, baseUnit: m.baseUnit,
    }, 'materialId');
    // Verfuegbarkeit im WERK DES AUFTRAGS (nicht activePlant): dahin geht die Komponente.
    materialStock(user, m.materialId, order.plant, role)
      .then((res) => setStock((res?.items || []).reduce((sum, s) => sum + (s.availableQty || 0), 0)))
      .catch(() => setStock('error'));
  };

  const reset = () => { setSelected(null); setStock(null); setQty(''); setQ(''); };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const res = await addOrderComponents(
        user, order.orderId,
        [{ materialId: selected.materialId, requiredQty: Number(qty), unit: selected.baseUnit }],
        idemKey.current, role,
      );
      idemKey.current = crypto.randomUUID();
      if (res?.ok) {
        setNotes((res.messages || []).map((m) => m.message));
        reset();
        onAdded();
      } else setError(res?.error || 'error');
    } catch (e) {
      setError(e?.message || 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button variant="secondary" className="!min-h-[48px] !text-base mt-2" onClick={() => setOpen(true)}>
        {t('pm.comp.add')}
      </Button>
    );
  }

  return (
    <Card className="space-y-3 mt-2">
      <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.comp.add')}</div>

      {!selected && (
        <div>
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('pm.materials.placeholder')}
            className={`w-full outline-none p-3 rounded-xl border transition-all duration-200 ${isDarkMode
              ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
              : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
            }`}
          />
          {results.length > 0 && (
            <div className="mt-2 space-y-1">
              {results.map((m) => (
                <button key={m.materialId} onClick={() => pick(m)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${isDarkMode ? 'hover:bg-white/[0.08] text-white/80' : 'hover:bg-slate-50 text-slate-700'}`}>
                  <span className="font-medium">{m.description}</span>
                  <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}> · {m.materialId}</span>
                </button>
              ))}
            </div>
          )}
          {/* Zuletzt verwendete Materialien — direkter Pick ohne Suche. */}
          {!q.trim() && <RecentMaterialChips user={user} max={10} onPick={pick} />}
        </div>
      )}

      {selected && (
        <>
          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <span className="font-medium">{selected.description}</span>
            <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}> · {selected.materialId}</span>
            <button onClick={reset} className={`ml-2 text-xs underline ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {t('pm.common.clear')}
            </button>
          </div>
          <div className={`text-sm ${stock === 0
            ? (isDarkMode ? 'text-red-300' : 'text-red-600')
            : (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')
          }`}>
            {stock === null ? t('pm.materials.loadingStock')
              : stock === 'error' ? t('pm.common.loadError')
              : `${t('pm.comp.stockAtPlant')} ${order.plant}: ${stock} ${selected.baseUnit || ''}`}
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.comp.shortfallHint')}</p>
          <label className="block">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.detail.qty')}</span>
            <input
              type="number" inputMode="decimal" min="0" step="1" value={qty}
              onChange={(e) => setQty(e.target.value)}
              className={`mt-1 w-full outline-none p-3 rounded-xl border transition-all duration-200 ${isDarkMode
                ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
                : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
              }`}
            />
          </label>
          <Button variant="confirm" className="!min-h-[48px] !text-base" onClick={submit}
            disabled={busy || !(Number(qty) > 0)}>
            {busy ? t('pm.comp.adding') : (<><Check size={18} /> {t('pm.comp.addBtn')}</>)}
          </Button>
        </>
      )}

      {notes.map((n, i) => (
        <p key={i} className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{n}</p>
      ))}
      {error && <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.order.errorPrefix')}: {error}</p>}
    </Card>
  );
}

// Arbeitsgang-Zeile mit aufklappbarer Zeit-Rueckmeldung (nur wenn canConfirm und nicht bereits CNF).
function OperationRow({ user, role, orderId, op, canConfirm, onPosted }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState('');
  const [final, setFinal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const submit = async () => {
    setBusy(true); setError('');
    try {
      // Monolith-API: `user` zuerst, Body direkt.
      const res = await confirmOperation(
        user,
        orderId,
        { operationNo: op.operationNo, actualWork: Number(hours), workUnit: op.workUnit || 'H', finalConfirmation: final },
        idemKey,
        role,
      );
      if (res?.ok) {
        setNote(res.idempotent ? t('pm.detail.confirmReplayed') : t('pm.detail.confirmPosted'));
        setIdemKey(crypto.randomUUID()); // naechste Rueckmeldung ist eine NEUE logische Aktion -> neuer Key
        setHours(''); setFinal(false); setOpen(false);
        onPosted();
      } else {
        // Business-Failure: sicher nichts gebucht; der Nutzer korrigiert vor dem Retry vermutlich
        // die Stunden -> neuer Key (wie die Wizards). Geworfene Transportfehler behalten den Key.
        setIdemKey(crypto.randomUUID());
        setError(res?.error || 'error');
      }
    } catch (e) {
      setError(e?.message || 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmable = canConfirm && op.status !== 'CNF';

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{op.operationNo} · {op.description}</div>
          <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {op.confirmedWork || 0}/{op.plannedWork} {op.workUnit} {t('pm.detail.confirmedShort')}
          </div>
        </div>
        <StatusBadge status={op.status} />
      </div>
      {note && <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{note}</p>}
      {confirmable && !open && (
        <Button variant="secondary" className="!min-h-[48px] !text-base" onClick={() => setOpen(true)}>
          {t('pm.detail.confirmSubmit')}
        </Button>
      )}
      {confirmable && open && (
        <div className={`space-y-3 border-t pt-3 ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <label className="block">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('pm.detail.hours')}</span>
            <input
              type="number" inputMode="decimal" min="0" step="0.25" value={hours}
              onChange={(e) => setHours(e.target.value)}
              className={`mt-1 w-full outline-none p-3 text-lg rounded-xl border transition-all duration-200 ${isDarkMode
                ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
                : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
              }`}
            />
          </label>
          <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <input type="checkbox" checked={final} onChange={(e) => setFinal(e.target.checked)} className="w-5 h-5 accent-brand" />
            {t('pm.detail.finalConfirmation')}
          </label>
          {error && <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.order.errorPrefix')}: {error}</p>}
          {/* Number(hours) > 0 statt <= 0-Vergleich: faengt auch NaN ("abc") ab. 0 Stunden sind
              NUR zusammen mit der Endrueckmeldung erlaubt (spiegelt die Server-Validierung). */}
          <Button variant="confirm" className="!min-h-[52px] !text-base" onClick={submit}
            disabled={busy || hours === '' || !(Number(hours) > 0 || (final && Number(hours) === 0))}>
            {busy ? t('pm.detail.confirming') : (<><Check size={18} /> {t('pm.detail.confirmSubmit')}</>)}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default OrderDetail;
